import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';

type UtilityStat = {
  label: string;
  value: string;
  icon: string;
  tone: 'blue' | 'green' | 'amber' | 'violet';
};

type UtilitySummaryRow = {
  code: string;
  name: string;
  category: 'DISCOM' | 'Trader' | 'Both';
  state: string;
  status: 'Active' | 'Inactive';
};

type UtilityUnitRow = {
  unitCode: string;
  unitName: string;
  pssCode: string;
  pssName: string;
  generatingCompanyCode: string;
  generatingCompanyName: string;
  generatorCode: string;
  generatorName: string;
  contractType: string;
  contractedCapacityMw: number;
  contractPeriod: string;
  oaBuyerType: string;
};

type UtilityMappingRecord = Record<string, string[]>;

type UtilityMappingMock = {
  header: {
    title: string;
    description: string;
  };
  filters: {
    category: string[];
    state: string[];
    status: string[];
  };
  utilities: UtilitySummaryRow[];
  unitCatalog: UtilityUnitRow[];
  utilityUnitMappings: UtilityMappingRecord;
};

type UtilityDisplayRow = UtilitySummaryRow & {
  mappedUnitCount: number;
  mappedPssCount: number;
  mappedCompanyCount: number;
  contractCount: number;
};

const DEFAULT_DATA: UtilityMappingMock = {
  header: {
    title: 'Manage Utility Mapping',
    description: 'Map utility contracts to PSS-linked generating units.'
  },
  filters: {
    category: ['All'],
    state: ['All'],
    status: ['All']
  },
  utilities: [],
  unitCatalog: [],
  utilityUnitMappings: {}
};

@Component({
  selector: 'app-managing-utility',
  standalone: true,
  templateUrl: './managing-utility.component.html',
  styleUrls: ['./managing-utility.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManagingUtilityComponent {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);

  readonly pageData = signal<UtilityMappingMock>(DEFAULT_DATA);
  readonly searchTerm = signal('');
  readonly selectedCategory = signal('All');
  readonly selectedState = signal('All');
  readonly selectedStatus = signal('All');
  readonly currentPage = signal(1);
  readonly pageSize = signal(8);

  readonly isManageMappingModalOpen = signal(false);
  readonly selectedUtilityCode = signal<string | null>(null);
  readonly activeUnitTab = signal<'available' | 'mapped'>('available');
  readonly availableUnitSearch = signal('');
  readonly mappedUnitSearch = signal('');
  readonly selectedAvailableUnitCodes = signal<string[]>([]);
  readonly selectedMappedUnitCodes = signal<string[]>([]);

  readonly title = computed(() => this.route.snapshot.data['title'] as string || this.pageData().header.title);
  readonly description = computed(() => this.route.snapshot.data['description'] as string || this.pageData().header.description);
  readonly filters = computed(() => this.pageData().filters);
  readonly utilities = computed(() => this.pageData().utilities);
  readonly unitCatalog = computed(() => this.pageData().unitCatalog);
  readonly utilityMappings = computed(() => this.pageData().utilityUnitMappings);

  readonly utilityRows = computed<UtilityDisplayRow[]>(() => {
    const catalogByCode = new Map(this.unitCatalog().map((unit) => [unit.unitCode, unit]));

    return this.utilities().map((utility) => {
      const unitCodes = this.utilityMappings()[utility.code] || [];
      const mappedUnits = unitCodes
        .map((code) => catalogByCode.get(code))
        .filter((unit): unit is UtilityUnitRow => Boolean(unit));

      return {
        ...utility,
        mappedUnitCount: mappedUnits.length,
        mappedPssCount: new Set(mappedUnits.map((unit) => unit.pssCode)).size,
        mappedCompanyCount: new Set(mappedUnits.map((unit) => unit.generatingCompanyCode)).size,
        contractCount: mappedUnits.length
      };
    });
  });

  readonly stats = computed<UtilityStat[]>(() => {
    const rows = this.utilityRows();
    const mappedUtilities = rows.filter((row) => row.mappedUnitCount > 0).length;
    const mappedUnits = rows.reduce((sum, row) => sum + row.mappedUnitCount, 0);
    const activeContracts = rows.reduce((sum, row) => sum + row.contractCount, 0);
    const oaMappings = this.unitCatalog().filter((unit) => unit.oaBuyerType === 'Open Access').length;

    return [
      { label: 'Total Utilities', value: String(rows.length), icon: '◫', tone: 'blue' },
      { label: 'Mapped Utilities', value: String(mappedUtilities), icon: '◎', tone: 'green' },
      { label: 'Mapped Units', value: String(mappedUnits), icon: '▣', tone: 'amber' },
      { label: 'Active Contracts', value: String(activeContracts), icon: '▤', tone: 'violet' },
      { label: 'Open Access Mappings', value: String(oaMappings), icon: '◌', tone: 'blue' }
    ];
  });

  readonly filteredRows = computed(() => {
    const search = this.searchTerm().trim().toLowerCase();
    const category = this.selectedCategory();
    const state = this.selectedState();
    const status = this.selectedStatus();

    return this.utilityRows().filter((row) => {
      const matchesSearch = !search || [row.code, row.name].some((value) => value.toLowerCase().includes(search));
      const matchesCategory = category === 'All' || row.category === category;
      const matchesState = state === 'All' || row.state === state;
      const matchesStatus = status === 'All' || row.status === status;
      return matchesSearch && matchesCategory && matchesState && matchesStatus;
    });
  });

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredRows().length / this.pageSize())));
  readonly paginatedRows = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.filteredRows().slice(start, start + this.pageSize());
  });
  readonly pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));
  readonly showingText = computed(() => {
    const total = this.filteredRows().length;
    if (total === 0) {
      return 'Showing 0 entries';
    }
    const start = ((this.currentPage() - 1) * this.pageSize()) + 1;
    const end = Math.min(this.currentPage() * this.pageSize(), total);
    return `Showing ${start} to ${end} of ${total} entries`;
  });

  readonly selectedUtility = computed(() => this.utilityRows().find((row) => row.code === this.selectedUtilityCode()) || null);
  readonly mappedUnits = computed(() => {
    const utilityCode = this.selectedUtilityCode();
    if (!utilityCode) {
      return [] as UtilityUnitRow[];
    }

    const mappedCodes = new Set(this.utilityMappings()[utilityCode] || []);
    return this.unitCatalog().filter((unit) => mappedCodes.has(unit.unitCode));
  });
  readonly availableUnits = computed(() => {
    const utilityCode = this.selectedUtilityCode();
    if (!utilityCode) {
      return [] as UtilityUnitRow[];
    }

    const mappedCodes = new Set(this.utilityMappings()[utilityCode] || []);
    return this.unitCatalog().filter((unit) => !mappedCodes.has(unit.unitCode));
  });
  readonly filteredAvailableUnits = computed(() => {
    const search = this.availableUnitSearch().trim().toLowerCase();
    return this.availableUnits().filter((unit) =>
      !search || [
        unit.unitCode,
        unit.unitName,
        unit.pssName,
        unit.generatingCompanyName,
        unit.generatorName
      ].some((value) => value.toLowerCase().includes(search))
    );
  });
  readonly filteredMappedUnits = computed(() => {
    const search = this.mappedUnitSearch().trim().toLowerCase();
    return this.mappedUnits().filter((unit) =>
      !search || [
        unit.unitCode,
        unit.unitName,
        unit.pssName,
        unit.generatingCompanyName,
        unit.generatorName
      ].some((value) => value.toLowerCase().includes(search))
    );
  });

  constructor() {
    this.http.get<UtilityMappingMock>('json/managing-utility.mock.json').subscribe({
      next: (data) => this.pageData.set(data),
      error: () => this.pageData.set(DEFAULT_DATA)
    });
  }

  updateSearch(value: string): void {
    this.searchTerm.set(value);
    this.currentPage.set(1);
  }

  updateCategory(value: string): void {
    this.selectedCategory.set(value);
    this.currentPage.set(1);
  }

  updateState(value: string): void {
    this.selectedState.set(value);
    this.currentPage.set(1);
  }

  updateStatus(value: string): void {
    this.selectedStatus.set(value);
    this.currentPage.set(1);
  }

  resetFilters(): void {
    this.searchTerm.set('');
    this.selectedCategory.set('All');
    this.selectedState.set('All');
    this.selectedStatus.set('All');
    this.currentPage.set(1);
  }

  goToPage(page: number): void {
    this.currentPage.set(Math.min(Math.max(page, 1), this.totalPages()));
  }

  openManageMappingModal(utilityCode: string): void {
    this.selectedUtilityCode.set(utilityCode);
    this.activeUnitTab.set('available');
    this.availableUnitSearch.set('');
    this.mappedUnitSearch.set('');
    this.selectedAvailableUnitCodes.set([]);
    this.selectedMappedUnitCodes.set([]);
    this.isManageMappingModalOpen.set(true);
  }

  closeManageMappingModal(): void {
    this.isManageMappingModalOpen.set(false);
    this.selectedUtilityCode.set(null);
    this.selectedAvailableUnitCodes.set([]);
    this.selectedMappedUnitCodes.set([]);
  }

  switchUnitTab(tab: 'available' | 'mapped'): void {
    this.activeUnitTab.set(tab);
    this.selectedAvailableUnitCodes.set([]);
    this.selectedMappedUnitCodes.set([]);
  }

  updateAvailableUnitSearch(value: string): void {
    this.availableUnitSearch.set(value);
  }

  updateMappedUnitSearch(value: string): void {
    this.mappedUnitSearch.set(value);
  }

  toggleAvailableSelection(unitCode: string, checked: boolean): void {
    this.selectedAvailableUnitCodes.update((codes) =>
      checked ? [...codes, unitCode] : codes.filter((code) => code !== unitCode)
    );
  }

  toggleMappedSelection(unitCode: string, checked: boolean): void {
    this.selectedMappedUnitCodes.update((codes) =>
      checked ? [...codes, unitCode] : codes.filter((code) => code !== unitCode)
    );
  }

  addSelectedUnits(): void {
    const utilityCode = this.selectedUtilityCode();
    if (!utilityCode || this.selectedAvailableUnitCodes().length === 0) {
      return;
    }

    this.pageData.update((data) => ({
      ...data,
      utilityUnitMappings: {
        ...data.utilityUnitMappings,
        [utilityCode]: [...new Set([...(data.utilityUnitMappings[utilityCode] || []), ...this.selectedAvailableUnitCodes()])]
      }
    }));

    this.selectedAvailableUnitCodes.set([]);
    this.activeUnitTab.set('mapped');
  }

  removeSelectedUnits(): void {
    const utilityCode = this.selectedUtilityCode();
    if (!utilityCode || this.selectedMappedUnitCodes().length === 0) {
      return;
    }

    const selected = new Set(this.selectedMappedUnitCodes());
    this.pageData.update((data) => ({
      ...data,
      utilityUnitMappings: {
        ...data.utilityUnitMappings,
        [utilityCode]: (data.utilityUnitMappings[utilityCode] || []).filter((code) => !selected.has(code))
      }
    }));

    this.selectedMappedUnitCodes.set([]);
  }

  removeMappedUnit(unitCode: string): void {
    const utilityCode = this.selectedUtilityCode();
    if (!utilityCode) {
      return;
    }

    this.pageData.update((data) => ({
      ...data,
      utilityUnitMappings: {
        ...data.utilityUnitMappings,
        [utilityCode]: (data.utilityUnitMappings[utilityCode] || []).filter((code) => code !== unitCode)
      }
    }));
  }

  formatCapacity(value: number): string {
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(value);
  }
}
